import { ButtonProps, Loader, MenuItemProps, TopNavigation, TopNavigationProps } from '@trackman/web-shared-components';
import { DefaultRoutes } from 'Routes';
import AppSwitcher from 'components/AppSwitcher/AppSwitcher';
import { Can, Cannot } from 'components/Can';
import LoggedOutInfoModal from 'components/LoggedOutInfoModal.tsx/LoggedOutInfoModal';
import { Notifications } from 'components/Notifications';
import ProfileIconMenu from 'components/ProfileIconMenu/ProfileIconMenu';
import TitleBar from 'components/TitleBar/TitleBar';
import { AppPermissions } from 'constants/AppPermissions';
import { useStores } from 'index';
import { observer } from 'mobx-react-lite';
import Forbidden from 'pages/Forbidden/Forbidden';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ApplicationUserRights } from 'stores/ApplicationUserRightsStore';
import { LayoutContentContainer, Spinner, StyledTopNavigation } from './LayoutStyles';
import DarkModeSwitch from 'components/darkMode/DarkModeSwitch';

type Props = {
  children: React.ReactNode;
};

export type TopLevelMenuLabel = 'Home' | 'Delivery';

export type Breadcrumb = { title?: string; subtitle?: string; backBtn: boolean; parent?: string; breadcrumbTitle: string };

type Navigation = Array<
  {
    label: TopLevelMenuLabel;
  } & (
    | ({
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
        href: string;
        need: string[];
        submenu?: undefined;
      } & ButtonProps)
    | {
        onClick?: undefined;
        href?: undefined;
        need?: undefined;
        submenu: ({
          label: string;
          onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
          href: string;
          need: string[];
        } & MenuItemProps)[];
      }
  )
>;

type ValueOf<T> = T[keyof T];
type DefaultRoute = ValueOf<typeof DefaultRoutes>;

const routeToActiveLabel: Record<DefaultRoute, TopLevelMenuLabel> = {
  '/': 'Home',
  '/delivery': 'Delivery',
};

export const activePageTitle: Record<DefaultRoute, Breadcrumb> = {
  '/': {
    title: 'Define criteria to schedule game deliveries',
    backBtn: false,
    breadcrumbTitle: 'Define criteria to schedule game deliveries',
  },
  '/delivery': {
    title: 'Customize your delivery',
    backBtn: true,
    parent: '/',
    breadcrumbTitle: 'Customize your delivery',
  },
};

const Layout = observer(({ children }: Props) => {
  const ss = useStores().startupStore;
  const history = useHistory();
  const location = useLocation();

  const [displayContent, setDisplayContent] = useState(false);

  useEffect(() => {
    if (ss.hasBearerToken) setDisplayContent(true);
  }, [ss.hasBearerToken]);

  const menuStructure: Navigation = useMemo(() => [], [history]);

  const activeNavigationLabel = useMemo(() => {
    const route = location.pathname;
    return {
      label: routeToActiveLabel[route],
      content: activePageTitle[route],
      route: route,
    };
  }, [location.pathname]);

  const navigation: TopNavigationProps['navigation'] = useMemo(() => {
    if (!ss.hasBearerToken) return [];

    const menuStructureTemp = [...menuStructure];

    menuStructureTemp.forEach((item, index, object) => {
      if (item.need && !ApplicationUserRights.IsAllowed(item.need)) object.splice(index, 1);
      if (item.submenu) {
        item.submenu.forEach((subItem, index, object) => {
          if (!ApplicationUserRights.IsAllowed(subItem.need)) object.splice(index, 1);
        });
        if (item.submenu.length === 0) object.splice(index, 1);
      }
    });
    return menuStructureTemp;
  }, [ss.hasBearerToken, menuStructure]);

  return (
    <LayoutContentContainer className='layout-content-container'>
      <StyledTopNavigation className='top-navigation'>
        <TopNavigation
          appName='Live Dashboard'
          navigation={navigation}
          activeNavigationLabel={activeNavigationLabel.label}
          utilities={
            ss.hasBearerToken && !ss.isInitializing
              ? [
                  {
                    key: 'dark-mode',
                    component: <DarkModeSwitch />,
                  },
                  { key: 'profile-icon-menu', component: <ProfileIconMenu setDisplayContent={setDisplayContent} /> },
                  {
                    key: 'app-switcher-icon-menu',
                    component: <AppSwitcher setDisplayContent={setDisplayContent} />,
                  },
                ]
              : []
          }
          animated
          displayContent={displayContent}
          onLogoClick={() => history.push(DefaultRoutes.Home)}
        />
      </StyledTopNavigation>
      {!displayContent && (
        <Spinner className='spinner'>
          <Loader size='large' />
        </Spinner>
      )}
      {activeNavigationLabel.route === DefaultRoutes.Home && (
        <Can need={AppPermissions.PortalAccess}>
          <TitleBar />
        </Can>
      )}
      <div
        style={{
          opacity: displayContent ? 1 : 0,
          transition: 'opacity 0.3s',
          pointerEvents: displayContent ? 'auto' : 'none',
          paddingBottom: '76px',
        }}
        className={activeNavigationLabel.route === DefaultRoutes.Home ? 'container' : ''}
      >
        {ss.hasBearerToken && (
          <>
            <Notifications />
            <Can need={AppPermissions.PortalAccess}>{children}</Can>
            <Cannot need={AppPermissions.PortalAccess}>
              <Forbidden />
            </Cannot>
          </>
        )}
      </div>
      {ss.config && <LoggedOutInfoModal logoutUrl={ss.config.logoutUrl} />}
    </LayoutContentContainer>
  );
});

export default Layout;
